import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import SystemArchitecture from '../components/SystemArchitecture';

const CONTACT_EMAIL = 'admin@grorixia.com';

const LandingPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');

  const onWaitlistSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    const subject = encodeURIComponent('Grorixia waitlist');
    const body = encodeURIComponent(
      `Please add me to the early-access waitlist.\n\nEmail: ${trimmed}\n\nNote:\n${note.trim() || '(none)'}\n`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <header className="landing-header">
          <p className="landing-badge">Early access — waitlist only</p>
          <h1 className="landing-title">Grorixia</h1>
          <p className="landing-subtitle">
            AI stock &amp; crypto trading signals
          </p>
          <p className="landing-tagline">
            Public subscriptions are not open yet. Join the waitlist to be
            notified when signal access starts.
          </p>
        </header>

        <main className="landing-main">
          <section className="landing-section" aria-label="About Grorixia">
            <h2 className="about-heading">Who we are</h2>
            <p className="about-lead">
              Grorixia builds AI-assisted automated trading strategies for
              equities and crypto, then delivers live entry and exit signals.
              You place the trades; we run the research and signal stack. We do
              not take custody of funds or store your exchange API keys.
            </p>
            <p className="about-audience">
              For traders who want researched signals — not a delayed chart
              dashboard, and not a black-box bot that trades your account.
            </p>
          </section>

          <section className="landing-section" aria-label="What you get">
            <h2 className="about-heading">What you get</h2>
            <p className="section-lead">
              When early access opens, subscribers receive actionable alerts —
              not managed trading.
            </p>
            <ul className="get-list">
              <li>
                <strong>Signal content</strong> — symbol, direction, entry/exit
                context for stock and crypto strategies we research.
              </li>
              <li>
                <strong>Delivery channels</strong> — Telegram, webhook, and/or
                app (channels confirmed at launch).
              </li>
              <li>
                <strong>Your control</strong> — you choose size, venue, and
                whether to act. Orders stay on your side.
              </li>
            </ul>
          </section>

          <section className="landing-section" aria-label="How it works">
            <h2 className="about-heading">How it works</h2>
            <ol className="how-steps">
              <li>
                <span className="how-step-num">1</span>
                <div>
                  <h3>Ingest &amp; research</h3>
                  <p>
                    Live market data feeds research. AI assists backtests and
                    scoring; only rules that hold up in monitoring stay on.
                  </p>
                </div>
              </li>
              <li>
                <span className="how-step-num">2</span>
                <div>
                  <h3>Emit signals</h3>
                  <p>
                    When a rule fires, we push an entry or exit alert to your
                    chosen channel.
                  </p>
                </div>
              </li>
              <li>
                <span className="how-step-num">3</span>
                <div>
                  <h3>You trade</h3>
                  <p>
                    Review the signal and execute yourself. Grorixia does not
                    place orders for you.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section className="landing-section" aria-label="What we build">
            <h2 className="about-heading">What we build</h2>
            <div className="landing-products">
              <article className="product-card">
                <h3 className="product-title">AI strategy research</h3>
                <p className="product-description">
                  Ingest live market data, backtest and score rules with AI
                  assistance, and keep only strategies that survive production
                  monitoring.
                </p>
              </article>
              <article className="product-card">
                <h3 className="product-title">Live entry/exit signals</h3>
                <p className="product-description">
                  Push actionable alerts over Telegram, webhook, or app. You
                  decide size and venue; we do not take custody or trade on
                  your behalf.
                </p>
              </article>
              <article className="product-card">
                <h3 className="product-title">Shared data plane</h3>
                <p className="product-description">
                  Collectors, scoring, and signal delivery share one stack —
                  so the signal you get is the same rule we researched and
                  run internally.
                </p>
              </article>
            </div>
          </section>

          <section
            id="waitlist"
            className="landing-section landing-waitlist"
            aria-label="Waitlist"
          >
            <h2 className="about-heading">Join the waitlist</h2>
            <p className="section-lead">
              Public signal subscriptions are not available yet. Leave your
              email and we will contact you when early access opens.
            </p>
            <form className="waitlist-form" onSubmit={onWaitlistSubmit}>
              <label className="waitlist-label" htmlFor="waitlist-email">
                Email
              </label>
              <input
                id="waitlist-email"
                className="waitlist-input"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
              <label className="waitlist-label" htmlFor="waitlist-note">
                Note (optional)
              </label>
              <textarea
                id="waitlist-note"
                className="waitlist-textarea"
                name="note"
                rows={3}
                placeholder="Stocks, crypto, preferred channel…"
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
              />
              <button type="submit" className="cta-button waitlist-submit">
                Request early access
              </button>
              <p className="waitlist-hint">
                Opens your mail app to send to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                No account is created until we invite you.
              </p>
            </form>
          </section>

          <div className="landing-cta">
            <Link to="/en" className="cta-button">
              Open dashboard
            </Link>
          </div>
          <p className="cta-note">
            Dashboard is our internal / demo view — not a public signal
            subscription.
          </p>

          <section
            id="disclaimer"
            className="landing-section landing-disclaimer"
            aria-label="Disclaimer"
          >
            <h2 className="about-heading">Disclaimer</h2>
            <p>
              Grorixia provides research-driven trading signals for
              informational purposes only. This is not investment advice,
              solicitation, or a recommendation to buy or sell any asset.
              Trading equities and crypto involves substantial risk of loss.
              Past performance does not guarantee future results. You are
              solely responsible for your trading decisions.
            </p>
          </section>

          <details className="landing-architecture">
            <summary>Infrastructure (AWS target map)</summary>
            <p className="architecture-note">
              Optional detail for technical reviewers — how research, scoring,
              and delivery roles map onto AWS. Venues stay external.
            </p>
            <div className="architecture-body">
              <SystemArchitecture />
            </div>
          </details>

          <section
            id="privacy"
            className="landing-section landing-legal"
            aria-label="Privacy"
          >
            <h2 className="about-heading">Privacy</h2>
            <p>
              If you join the waitlist, we use the email (and optional note)
              you send only to contact you about early access. We do not sell
              personal data. We do not require exchange API keys for the
              signal product. For questions:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </section>

          <section
            id="terms"
            className="landing-section landing-legal"
            aria-label="Terms"
          >
            <h2 className="about-heading">Terms</h2>
            <p>
              Early access and any future signal service are provided as-is.
              Availability, channels, and coverage may change. You agree that
              signals are informational, that you execute trades at your own
              risk, and that Grorixia is not liable for trading losses. Full
              commercial terms will be published before paid subscriptions
              open.
            </p>
          </section>
        </main>

        <footer className="landing-footer" id="contact">
          <p className="footer-brand">
            &copy; {new Date().getFullYear()} Grorixia ·{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
          <nav className="footer-nav" aria-label="Footer">
            <a href="#waitlist">Waitlist</a>
            <a href="#disclaimer">Disclaimer</a>
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
          </nav>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
